/**
 * tsQuery — turns a raw `q` string into the two pieces the wall's matched segment reads: the `head` (whole words and
 * pills) and the `lastPrefix` (the last word, completed as a prefix), which `catalogue.ts` finds in memory and
 * `mine.server.ts` binds into `websearch_to_tsquery('english', head) && to_tsquery('english', lastPrefix || ':*')`
 * (storage plan § Reading the wall, `thinking/plans/2026_08_27_wall_storage.md:117`).
 *
 * Pure — no I/O, no Postgres, no `@core/database` import — so it (and its spec) never need
 * `DATABASE_URL` at all. Deliberately NOT co-located in `tiles.server.ts` despite the plan's own
 * Files table listing it there (`thinking/plans/2026_08_31_wall_plan_b_read_path.md:44`):
 * `tiles.server.ts` imports `@core/database/prisma.server`, which throws at module load if
 * `DATABASE_URL` is unset (confirmed by import probe, 2026-08-31) — importing it from a jest spec
 * with no DB configured fails the whole suite before a single assertion runs. `defaultRank.ts`
 * (plan a, same directory) already draws this line between a pure module and its `.server`
 * neighbour; this file follows that precedent instead of the plan's literal file placement.
 *
 * Also does the sanitizing the reviewer brief calls for (`thinking/plans/2026_08_31_wall_plan_b_
 * read_path.md`, § Reviewer brief): `lastPrefix` is concatenated into raw tsquery syntax
 * (`lastPrefix || ':*'`) in `mine.server.ts`'s SQL, so it must never carry a quote, an operator
 * (`&|!():`), or anything else `to_tsquery` would parse as syntax rather than a lexeme —
 * stripping to `\p{L}\p{N}` (Unicode letters/digits) is the one guarantee that holds regardless
 * of what the user typed.
 */

import {TOKEN_PATTERN} from './profile'

export type TsQuery = {
    /** Zero or more space-joined tokens, already sanitized, bound through `websearch_to_tsquery`
     * itself (which does its own operator/quote handling on the DB side) — empty string when
     * there is only one token. `&&` against an empty tsquery IS well-defined Postgres behaviour
     * (the empty operand is dropped: `'the' && 'koi:*'` → `'koi':*`, verified on dev,
     * fix-then-merge review item 7) — the caller (`tiles.server.ts`'s `tsqueryExpr`) still skips
     * the `websearch_to_tsquery(...) &&` half when `head` is empty, but that is an optimization
     * (one fewer function call per query), not a correctness requirement. */
    head: string
    /** The single sanitized token the matched segment turns into a prefix match; `null` when the
     * query is pills alone (a phrase is exact, nothing is left to complete). */
    lastPrefix: string | null
    /** The facet tokens the house has (`facets @> ARRAY[...]`), distinct and capped; absent when there are none. */
    facets?: string[]
}

// A 2 000-character `q` is one of the reviewer brief's named attacks — cap well before that so
// a pathological input can't inflate the `websearch_to_tsquery` call the DB layer builds from
// `head`. Wide enough that no genuine wall search is ever truncated (real queries in the field:
// "koi", "red ink", "wallpaper phone").
const MAX_QUERY_LENGTH = 200
// websearch_to_tsquery has no token-count cap of its own; bounding here keeps the parenthesized
// AND-expression it builds small regardless of how many words a caller crams into `q`.
const MAX_HEAD_TOKENS = 8

function sanitizeToken(token: string): string {
    return token.replace(/[^\p{L}\p{N}]+/gu, '')
}

// A pill is a phrase in double quotes inside `q`: `"indigo dye" fox`. At most this many count, so a
// hand-edited URL cannot inflate the tsquery any more than typed words can.
const MAX_PILLS = 5

/** A phrase made safe to sit between quotes: letters and digits and single spaces only. */
function sanitizePhrase(phrase: string): string {
    return phrase.replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
}

/** Two phrases are one pill: same letters and digits, whatever the case or the spacing. */
export function samePhrase(a: string, b: string): boolean {
    return sanitizePhrase(a).toLowerCase() === sanitizePhrase(b).toLowerCase()
}

export type ParsedQuery = {
    /** The quoted phrases, sanitized, distinct, in the order written. */
    pills: string[]
    /** The facet tokens (`key:value`, `profile.ts` `TOKEN_PATTERN`) written outside the quotes that `isFacet` accepts,
     *  distinct, in the order written. */
    facets: string[]
    /** Everything outside the quotes and the facet tokens, single-spaced, a trailing space kept (the field is typed
     *  into). A stray unbalanced quote is dropped. A `key:value` word `isFacet` refuses (`re:invent`) stays a word. */
    words: string
}

const anyToken = (token: string) => TOKEN_PATTERN.test(token)

/** `q` split into its pills and its typed words. The single reading of the syntax: the search field,
 * the ghost and the read path all take it from here. `isFacet` is the house's, where the caller knows it; without
 * it, any word of the token pattern is a facet token. */
export function parseQuery(q: string, isFacet: (token: string) => boolean = anyToken): ParsedQuery {
    const pills: string[] = []
    const facets: string[] = []
    const text = q
        .replace(/"([^"]*)"/g, (_, phrase: string) => {
            const clean = sanitizePhrase(phrase)
            if (clean && !pills.some((p) => samePhrase(p, clean))) pills.push(clean)
            return ' '
        })
        .replace(/"/g, ' ')
        .replace(/\s+/g, ' ')
        .trimStart()
    const kept: string[] = []
    for (const word of text.split(' ')) {
        if (!word) continue
        if (!TOKEN_PATTERN.test(word) || !isFacet(word)) kept.push(word)
        else if (!facets.includes(word)) facets.push(word)
    }
    const words = kept.length > 0 && text.endsWith(' ') ? `${kept.join(' ')} ` : kept.join(' ')
    return {pills, facets, words}
}

/** The inverse: pills first, quoted, then the facet tokens, then the words. */
export function composeQuery(pills: readonly string[], words: string, facets: readonly string[] = []): string {
    const quoted = pills.map((p) => sanitizePhrase(p)).filter(Boolean).map((p) => `"${p}"`)
    const tokens = [...new Set(facets.filter((f) => TOKEN_PATTERN.test(f)))]
    const rest = words.replace(/"/g, ' ').replace(/\s+/g, ' ').trimStart()
    return [...quoted, ...tokens, rest].filter((part) => part.length > 0).join(' ')
}

/** `null` = no usable search term at all (the storage plan's "empty q = segment 2 alone",
 * `2026_08_27_wall_storage.md:123` — and the same for a query that sanitizes down to nothing,
 * e.g. pure punctuation). */
export function buildTsQuery(q: string, isFacet: (token: string) => boolean = () => false): TsQuery | null {
    // Only the caller that reads `wall_tiles.facets` says which tokens are facets; a `key:value` word the house does
    // not have was typed as a word (`re:invent`) and is searched as one.
    const parsed = parseQuery(q.trim().slice(0, MAX_QUERY_LENGTH), isFacet)
    const {pills, words} = parsed
    const facets = parsed.facets.slice(0, MAX_PILLS)
    const tokens = words.split(/\s+/).map(sanitizeToken).filter((t) => t.length > 0)
    const quoted = pills.slice(0, MAX_PILLS).map((p) => `"${p}"`)
    if (tokens.length === 0 && quoted.length === 0 && facets.length === 0) return null

    const lastPrefix = tokens.length > 0 ? tokens[tokens.length - 1] : null
    const head = [...quoted, ...tokens.slice(0, -1).slice(0, MAX_HEAD_TOKENS)].join(' ')
    return facets.length > 0 ? {head, lastPrefix, facets} : {head, lastPrefix}
}

/**
 * The `q` a wall surface reads off its own URL — `GET /wall`, the wall route's SSR loader, and the
 * wall component's own search field, which must all agree on what the URL says or the first paint
 * answers a different query than the field shows (re-review N2: the loader read `q: ''` regardless
 * of the URL, so a `?q=koi` deep link rendered the default wall under a field saying "koi").
 *
 * Trim and cap only: `buildTsQuery` above owns the actual tokenizing and the Unicode-class
 * sanitizing of anything that reaches tsquery syntax, and it applies the same bound — this is the
 * one shared reading of the parameter, not a second sanitizer.
 */
export function readWallQueryParam(params: URLSearchParams): string {
    return (params.get('q') ?? '').trim().slice(0, MAX_QUERY_LENGTH)
}

/** `path` (a wall address, a return token or the bare `/`) with the wall's `?q=` carried along, so a
 *  sign-in round-trip comes back to the search it left. The same trim and cap as the reading above. */
export function withWallQuery(path: string, q: string): string {
    const query = q.trim().slice(0, MAX_QUERY_LENGTH)
    if (!query) return path
    const [base, search = ''] = path.split('?')
    const params = new URLSearchParams(search)
    params.set('q', query)
    return `${base}?${params}`
}
